import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationSettings } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { ApiError } from './settings-queries';

export type { NotificationSettings };

/** What GET /api/notification-settings answers — the person's choices, the Tips pause flag, and the revision to write against. */
export interface NotificationSettingsRead {
  settings: NotificationSettings;
  /** Recipient field 35 — set when the person asked not to be re-engaged (the "Tips" toggle). */
  reEngagementPausedAt: string | null;
  /** Recipient field 38 — the chat-language pick; `null` = automatic (the harness classifier owns it). */
  conversationLanguage: string | null;
  revision: string | null;
}

/**
 * One PATCH, up to three fields — the same split as the native-data bridge: `settings` (field 36, under
 * the revision the read returned), `reEngagementPaused` (the Tips switch, field 35) and
 * `conversationLanguage` (field 38; `null` hands it back to automatic). Pass only what changed.
 */
export interface NotificationSettingsWrite {
  settings?: NotificationSettings;
  revision?: string | null;
  reEngagementPaused?: boolean;
  conversationLanguage?: string | null;
}

export const notificationSettingsKey = ['settings', 'notification-settings'] as const;

/**
 * The person's explicit notification choices (Recipient field 36; docs/smart-notifications-design.md
 * §5.1), through `/api/notification-settings`. The UI (Decisions reminder channel · Results · Routine
 * updates · Tips · Quiet hours) arrives in step 4 on all three surfaces; this hook is its web plumbing,
 * beside the other settings queries so the cache stays the single source of truth.
 *
 * "Unset" in `settings` means the documented DEFAULT, never "off" — a renderer maps an absent field to
 * the default label rather than to a disabled switch. The write REPLACES the whole message under the
 * revision the read returned; the route and the recipient client re-read and retry on a conflict, so
 * callers pass the settings they want and nothing else.
 */
export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationSettingsKey,
    queryFn: async (): Promise<NotificationSettingsRead | null> => {
      const res = await apiFetch(`${API_BASE}/api/notification-settings`, { credentials: 'include' });
      // No Orion tenant yet — nothing to render; a poll-able null rather than a latched error.
      if (res.status === 404) return null;
      if (!res.ok) throw new ApiError(res.status);
      return (await res.json()) as NotificationSettingsRead;
    },
    staleTime: 60_000,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NotificationSettingsWrite): Promise<NotificationSettingsRead> => {
      const body: Record<string, unknown> = {};
      if (input.settings !== undefined) {
        body.settings = input.settings;
        if (input.revision) body.revision = input.revision;
      }
      if (input.reEngagementPaused !== undefined) body.reEngagementPaused = input.reEngagementPaused;
      if (input.conversationLanguage !== undefined) body.conversationLanguage = input.conversationLanguage;
      const res = await apiFetch(`${API_BASE}/api/notification-settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new ApiError(res.status);
      const data = (await res.json()) as {
        settings: NotificationSettings;
        reEngagementPausedAt: string | null;
        conversationLanguage: string | null;
        revision: string | null;
      };
      const prev = qc.getQueryData<NotificationSettingsRead>(notificationSettingsKey);
      return {
        settings: data.settings ?? prev?.settings ?? {},
        reEngagementPausedAt: data.reEngagementPausedAt ?? null,
        conversationLanguage: data.conversationLanguage ?? null,
        // A row-only write answers no settings revision; keep the one the read returned so the next
        // settings write still goes under it.
        revision: data.revision ?? prev?.revision ?? null,
      };
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationSettingsKey, data);
    },
  });
}
